import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api/client";
import { ShieldStar, Users, Pulse, Download, Bank, FolderOpen, Star, UserPlus, ArrowRight, Sparkle, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminPanel() {
  const [stats, setStats] = useState(null);
  const [recentAudit, setRecentAudit] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/audit-logs", { params: { limit: 8 } }).then(({ data }) => setRecentAudit(data || [])).catch(() => {});
    api.get("/users").then(({ data }) => setPendingUsers((data || []).filter((u) => u.status === "pending"))).catch(() => {});
  }, []);

  const exportLenders = async () => {
    try {
      const { data } = await api.get("/export/lenders");
      const csv = toCsv(data);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `lenders-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} lenders to CSV`);
    } catch { toast.error("Export failed"); }
  };

  const applyDefaults = async () => {
    if (!window.confirm("Fill in tier-default values (max term, buy rate, commission points, FICO, TIB) for any lender field that's currently empty? Manual edits will NOT be overwritten.")) return;
    try {
      const { data } = await api.post("/admin/apply-tier-defaults");
      const breakdown = Object.entries(data.by_tier || {}).map(([t, n]) => `${t}: ${n}`).join(", ");
      toast.success(`Updated ${data.updated} lenders${breakdown ? " · " + breakdown : ""}`);
    } catch { toast.error("Failed to apply defaults"); }
  };

  return (
    <div className="space-y-8" data-testid="admin-panel">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2 inline-flex items-center gap-1.5">
            <ShieldStar size={12} weight="fill" className="text-blue-600" /> Admin control
          </div>
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Admin panel</h1>
          <p className="text-slate-600 mt-1">Manage users, audit activity, export lender data, and oversee the platform.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={applyDefaults} data-testid="admin-apply-tier-defaults" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium">
            <Sparkle size={14} weight="fill" /> Apply tier defaults
          </button>
          <button onClick={exportLenders} data-testid="admin-export-lenders" className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 text-sm font-medium">
            <Download size={14} /> Export lender data
          </button>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatTile icon={Bank} label="Total lenders" value={stats?.total_lenders} />
        <StatTile icon={Star} label="Preferred" value={stats?.preferred_lenders} accent="bg-blue-50 text-blue-700" />
        <StatTile icon={FolderOpen} label="Saved deals" value={stats?.total_deals} />
        <StatTile icon={UserPlus} label="Pending approvals" value={stats?.pending_users} accent={stats?.pending_users ? "bg-amber-50 text-amber-700" : ""} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending approvals quick list */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Awaiting approval</div>
              <div className="font-heading text-lg font-bold">Pending users</div>
            </div>
            <Link to="/admin/users" className="text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1">
              Manage <ArrowRight size={14} />
            </Link>
          </div>
          {pendingUsers.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-8">No users awaiting approval.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {pendingUsers.map((u) => (
                <li key={u.id} className="py-3 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{u.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                  </div>
                  <Link to="/admin/users" className="text-xs font-medium text-blue-600 hover:text-blue-800">Review</Link>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent audit */}
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Activity</div>
              <div className="font-heading text-lg font-bold">Recent audit</div>
            </div>
            <Link to="/admin/audit" className="text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1">
              Full log <ArrowRight size={14} />
            </Link>
          </div>
          <ul className="space-y-3">
            {recentAudit.length === 0 && <li className="text-sm text-slate-500 text-center py-4">No activity yet.</li>}
            {recentAudit.map((a) => (
              <li key={a.id} className="flex items-start gap-2">
                <Pulse size={14} className="text-slate-400 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-slate-700 truncate"><span className="font-mono text-[10px] text-slate-500 mr-1">{a.action}</span>{a.actor_email}</div>
                  <div className="text-[10px] text-slate-400">{new Date(a.created_at).toLocaleString()}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <QuickCard to="/admin/users" icon={Users} title="User management" subtitle="Approve signups, change roles, disable accounts" />
        <QuickCard to="/admin/api-keys" icon={Key} title="API keys" subtitle="Generate keys so other apps can call our litigation search" />
        <QuickCard to="/admin/audit" icon={Pulse} title="Audit log" subtitle="Every action across the platform" />
        <QuickCard to="/lenders/new" icon={Bank} title="Add lender" subtitle="Add a new lender to the database" />
      </div>
    </div>
  );
}

const StatTile = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-6">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{label}</div>
      <div className={`h-8 w-8 rounded-md grid place-items-center ${accent || "bg-slate-100 text-slate-700"}`}>
        <Icon size={16} weight="duotone" />
      </div>
    </div>
    <div className="font-heading text-4xl font-bold tracking-tighter text-slate-900">{value ?? "—"}</div>
  </div>
);

const QuickCard = ({ to, icon: Icon, title, subtitle }) => (
  <Link to={to} className="group bg-white border border-slate-200 rounded-lg p-6 hover:shadow-sm transition-all">
    <Icon size={22} weight="duotone" className="text-slate-700" />
    <div className="font-heading text-lg font-bold mt-3 text-slate-900">{title}</div>
    <p className="text-xs text-slate-600 mt-1">{subtitle}</p>
    <div className="mt-3 inline-flex items-center text-xs font-medium text-blue-600">
      Open <ArrowRight size={12} className="ml-0.5 transition-transform group-hover:translate-x-0.5" />
    </div>
  </Link>
);

function toCsv(rows) {
  if (!rows || !rows.length) return "";
  const cols = ["name","tier","status","is_hot","contact_name","contact_email","contact_phone",
    "industries_funded","preferred_industries","restricted_industries",
    "min_term_months","max_term_months","min_funding","max_funding","preferred_funding",
    "min_credit_score","min_time_in_business_months","min_monthly_revenue","min_avg_daily_balance",
    "max_negative_days","max_nsfs","commission_points","commission_structure","fees",
    "buy_sell_rate_notes","states_funded","states_not_funded","product_types","required_documents",
    "speed_to_fund","renewal_policy","stacking_policy","preferred_deal_profile","red_flags","notes",
    "created_at","updated_at","updated_by"];
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = Array.isArray(v) ? v.join("; ") : typeof v === "object" ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.join(",");
  const lines = rows.map((r) => cols.map((c) => esc(r[c])).join(","));
  return [header, ...lines].join("\n");
}

